/**
 * The last game this player started or joined on this device, local or online.
 *
 * The Resume door offers one game, and Mitch's rule picks it (2026-09-30): an
 * unfinished game stays resumable until the player starts or joins another one.
 * The two kinds of game live in different places, local ones in IndexedDB and
 * online ones on the server, so neither store can say which came last. This
 * one line can, because it is written where a game actually begins: a local
 * table dealing, or an online seat being taken.
 *
 * It is not the Play again record in `settingsStore`. That one remembers what
 * to replay, and skips every start it cannot replay, such as a private table
 * or a join by code, which are exactly the joins this has to see.
 *
 * `localStorage` rather than IndexedDB: it is one small record per player, it
 * is read on the home screen's first frame, and it must not wait on a database
 * open. Every access is best-effort, like the settings store: private browsing
 * or a full disk loses the marker, and the home then falls back to the newest
 * unfinished local game.
 */

export type StartedGameKind = 'local' | 'online';

export interface StartedGame {
  readonly kind: StartedGameKind;
  /** The local record id, or the online game id. */
  readonly id: string;
  /** When it started or was joined, as an ISO time from this device's clock. */
  readonly at: string;
}

const PREFIX = 'cribbage.lastStarted.';

/** One marker per player, so a shared device keeps each player's own. */
function keyFor(playerId: string | null): string {
  return `${PREFIX}${playerId ?? ''}`;
}

export function markStarted(
  playerId: string | null,
  game: { kind: StartedGameKind; id: string },
  at: Date = new Date(),
): void {
  const record: StartedGame = { kind: game.kind, id: game.id, at: at.toISOString() };
  try {
    globalThis.localStorage?.setItem(keyFor(playerId), JSON.stringify(record));
  } catch {
    // Storage denied or full: the home falls back to the newest local game.
  }
}

/** Drops this player's marker, for a player whose data was deleted. */
export function forgetStarted(playerId: string): void {
  try {
    globalThis.localStorage?.removeItem(keyFor(playerId));
  } catch {
    // Storage denied: there is no marker to leave behind.
  }
}

export function lastStarted(playerId: string | null): StartedGame | null {
  try {
    const raw = globalThis.localStorage?.getItem(keyFor(playerId)) ?? null;
    return raw === null ? null : parse(raw);
  } catch {
    return null;
  }
}

/** A hand-edited or older value reads as no marker rather than a wrong one. */
function parse(raw: string): StartedGame | null {
  const value: unknown = JSON.parse(raw);
  if (typeof value !== 'object' || value === null) return null;
  const { kind, id, at } = value as Record<string, unknown>;
  if (kind !== 'local' && kind !== 'online') return null;
  if (typeof id !== 'string' || id === '' || typeof at !== 'string') return null;
  if (Number.isNaN(Date.parse(at))) return null;
  return { kind, id, at };
}
